import {
  describeFieldError,
  problemFieldErrors,
  type ProblemFieldError,
} from "../shared/validation";

export type ProblemDetail = {
  title: string;
  detail: string;
  status: number;
  message?: string;
  requestId?: string;
  errors?: Record<string, string[]>;
  fieldErrors?: ProblemFieldError[];
  /** 409 from adding a lead: the lead that already holds the website or address. */
  existingLeadId?: string;
  /** Quick send: the lead that was created even though the email was refused. */
  leadId?: string;
};

const NETWORK_FAILURE = "The portal could not be reached. Check your connection and try again.";

/**
 * The sentence to show a person for a problem response: the first field error
 * as "Company name: …" (plus how many other fields need attention), otherwise
 * the problem detail.
 */
export function problemMessage(problem: ProblemDetail): string {
  const [first, ...rest] = problemFieldErrors(problem);
  if (!first) return problem.detail;
  const text = describeFieldError(first);
  const others = new Set(rest.map((entry) => entry.field).filter((field) => field !== first.field));
  if (others.size === 0) return text;
  return `${text} (${others.size} more ${others.size === 1 ? "field needs" : "fields need"} attention)`;
}

export class ApiError extends Error {
  readonly problem: ProblemDetail;
  constructor(problem: ProblemDetail) {
    super(problemMessage(problem));
    this.name = "ApiError";
    this.problem = problem;
  }
}

function isMutation(method: string | undefined) {
  return ["POST", "PUT", "PATCH", "DELETE"].includes((method ?? "GET").toUpperCase());
}

function canRetryBody(body: BodyInit | null | undefined) {
  return (
    body === undefined ||
    body === null ||
    typeof body === "string" ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    ArrayBuffer.isView(body) ||
    body instanceof FormData ||
    body instanceof URLSearchParams
  );
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body && !headers.has("content-type") && typeof init.body === "string") {
    headers.set("content-type", "application/json");
  }
  const mutation = isMutation(init?.method);
  if (mutation && path.startsWith("/api/") && !path.startsWith("/api/auth/")) {
    if (!headers.has("idempotency-key")) headers.set("idempotency-key", crypto.randomUUID());
  }
  const request: RequestInit = {
    credentials: "include",
    ...init,
    headers,
  };
  let response: Response;
  try {
    response = await fetch(path, request);
  } catch (error) {
    if (!mutation || !headers.has("idempotency-key") || !canRetryBody(init?.body)) throw error;
    response = await fetch(path, request);
  }
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const candidate = body as Partial<ProblemDetail> | null;
    throw new ApiError({
      title: candidate?.title ?? "Request failed",
      detail:
        candidate?.detail ??
        candidate?.message ??
        candidate?.title ??
        "The request could not be completed.",
      status: response.status,
      ...(candidate?.requestId ? { requestId: candidate.requestId } : {}),
      ...(candidate?.errors ? { errors: candidate.errors } : {}),
      ...(Array.isArray(candidate?.fieldErrors) ? { fieldErrors: candidate.fieldErrors } : {}),
      ...(typeof candidate?.existingLeadId === "string"
        ? { existingLeadId: candidate.existingLeadId }
        : {}),
      ...(typeof candidate?.leadId === "string" ? { leadId: candidate.leadId } : {}),
    });
  }
  return body as T;
}

function isNetworkFailure(error: unknown) {
  return (
    error instanceof TypeError &&
    /failed to fetch|networkerror|load failed|network request failed/iu.test(error.message)
  );
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return problemMessage(error.problem);
  if (isNetworkFailure(error)) return NETWORK_FAILURE;
  if (error instanceof Error) return error.message;
  return "The request could not be completed.";
}
